// src/pages/payments/StripeOnboardingComplete.jsx
// Landing page for Stripe Connect onboarding return.
// Refreshes studio data and confirms payouts connection status.

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useActiveStudioId } from "../../hooks/useActiveStudioId";
import { useStudio } from "../../contexts/StudioContext";
import { authedFetch } from "../../lib/authedFetch";
import { useAuth } from "../../contexts/AuthContext";

export default function StripeOnboardingComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const { studioId } = useActiveStudioId();
  const { studio, updateStudio } = useStudio() || {};
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Use current user from centralized auth context (no direct supabase.auth call)
        const userId = user?.id || null;
        if (!userId) {
          setError("You are not signed in.");
          setLoading(false);
          return;
        }

        // Hit backend status endpoint to ensure latest Stripe status is reflected
        try {
          const statusRes = await authedFetch(`/api/stripe/connect-status`, { method: "GET" }).catch(() => null);
          var statusFromApi = null;
          if (statusRes) {
            try {
              const j = await statusRes.json();
              statusFromApi = j?.status || null;
            } catch {}
          }
        } catch {}

        // Fetch studio row by id if available; otherwise by owner_user_id
        let studioRow = null;
        if (studioId) {
          const { data, error } = await supabase
            .from("studios")
            .select("id, stripe_account_id_resolved, stripe_account_status")
            .eq("id", studioId)
            .maybeSingle();
          if (error) throw error;
          studioRow = data;
        } else {
          const { data, error } = await supabase
            .from("studios")
            .select("id, stripe_account_id_resolved, stripe_account_status")
            .eq("owner_user_id", userId)
            .limit(1)
            .single();
          if (error) throw error;
          studioRow = data;
        }

        const sid = studioRow?.id || studioId || studio?.studioId || null;
        const stripeId = studioRow?.stripe_account_id_resolved || null;
        const stripeStatus = statusFromApi || studioRow?.stripe_account_status || null;

        // Mirror into StudioContext so UI picks up connection state
        if (typeof updateStudio === "function") {
          await updateStudio({
            studioId: sid || studio?.studioId,
            stripe_account_id_resolved: stripeId || null,
            stripe_account_status: stripeStatus || (stripeId ? "enabled" : "not_connected"),
          });
        }

        setConnected(!!stripeId);
        setLoading(false);
      } catch (err) {
        console.error("[StripeOnboardingComplete] refresh failed", err);
        setError(err?.message || "Failed to update Stripe connection status");
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studioId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/80">Updating your studio…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white px-4">
      <div className="max-w-md w-full p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl text-center">
        {connected ? (
          <>
            <div className="text-2xl font-semibold mb-2">Stripe payouts updated</div>
            <p className="text-white/80 mb-6">
              You can now receive payments for offers, workshops and studio space bookings.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => navigate("/dashboard/home", { replace: true })}
                className="px-4 py-2 rounded-xl bg-amber-500 text-black font-semibold"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => navigate("/dashboard/settings", { replace: true })}
                className="px-4 py-2 rounded-xl bg-white/10 border border-white/15"
              >
                Open Settings
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-2xl font-semibold mb-2">We couldn’t confirm your Stripe connection</div>
            <p className="text-white/80 mb-6">
              Please try again from the Studio settings.
            </p>
            <button
              onClick={() => navigate("/dashboard/settings", { replace: true })}
              className="px-4 py-2 rounded-xl bg-amber-500 text-black font-semibold"
            >
              Open Studio Settings
            </button>
          </>
        )}
      </div>
    </div>
  );
}
